import { useCallback, useEffect, useRef, useState } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

const STUN_SERVERS: RTCConfiguration = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

const getPairKey = (userA: string, userB: string) => [userA, userB].sort().join(':');

type VoicePhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active';

type PendingOffer = {
  callId: string;
  offer: RTCSessionDescriptionInit;
};

type CallSignalPayload = {
  from: string;
  callId: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  reason?: string;
};

export function useDmVoiceCall(selfId?: string | null, otherId?: string | null) {
  const [phase, setPhase] = useState<VoicePhase>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingOfferRef = useRef<PendingOffer | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const activeCallIdRef = useRef<string | null>(null);
  const subscribedRef = useRef(false);

  const clearMedia = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
  }, []);

  const teardownPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.onicecandidate = null;
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.onconnectionstatechange = null;
      peerConnectionRef.current.close();
    }

    peerConnectionRef.current = null;
    pendingCandidatesRef.current = [];
  }, []);

  const resetCall = useCallback(() => {
    teardownPeerConnection();
    clearMedia();
    pendingOfferRef.current = null;
    activeCallIdRef.current = null;
    setPhase('idle');
    setIsMuted(false);
  }, [clearMedia, teardownPeerConnection]);

  const sendSignal = useCallback(
    async (event: 'voice-offer' | 'voice-answer' | 'voice-ice' | 'voice-end', payload: CallSignalPayload) => {
      if (!channelRef.current || !subscribedRef.current) {
        return;
      }

      await channelRef.current.send({
        type: 'broadcast',
        event,
        payload,
      });
    },
    []
  );

  const createPeerConnection = useCallback(
    async (callId: string) => {
      const connection = new RTCPeerConnection(STUN_SERVERS);
      peerConnectionRef.current = connection;

      connection.onicecandidate = (event) => {
        if (!event.candidate || !selfId) {
          return;
        }

        void sendSignal('voice-ice', {
          from: selfId,
          callId,
          candidate: event.candidate.toJSON(),
        });
      };

      connection.ontrack = (event) => {
        const [stream] = event.streams;
        if (remoteAudioRef.current && stream) {
          remoteAudioRef.current.srcObject = stream;
          void remoteAudioRef.current.play().catch(() => undefined);
        }
      };

      connection.onconnectionstatechange = () => {
        const state = connection.connectionState;
        if (state === 'connected') {
          setPhase('active');
          setError(null);
          return;
        }

        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          resetCall();
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !isMuted;
        connection.addTrack(track, stream);
      });

      return connection;
    },
    [isMuted, resetCall, selfId, sendSignal]
  );

  const applyPendingCandidates = useCallback(async () => {
    if (!peerConnectionRef.current || !pendingCandidatesRef.current.length) {
      return;
    }

    for (const candidate of pendingCandidatesRef.current) {
      await peerConnectionRef.current.addIceCandidate(candidate);
    }

    pendingCandidatesRef.current = [];
  }, []);

  const startCall = useCallback(async () => {
    if (!selfId || !otherId || selfId === otherId) {
      return;
    }

    if (!subscribedRef.current) {
      setError('Voice channel is still connecting. Please try again in a moment.');
      return;
    }

    try {
      setError(null);
      setPhase('outgoing');
      const callId = crypto.randomUUID();
      activeCallIdRef.current = callId;
      const connection = await createPeerConnection(callId);
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);
      setPhase('connecting');

      await sendSignal('voice-offer', {
        from: selfId,
        callId,
        offer,
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to start the voice call.');
      resetCall();
    }
  }, [createPeerConnection, otherId, resetCall, selfId, sendSignal]);

  const acceptCall = useCallback(async () => {
    if (!selfId || !pendingOfferRef.current) {
      return;
    }

    try {
      setError(null);
      setPhase('connecting');
      const { callId, offer } = pendingOfferRef.current;
      activeCallIdRef.current = callId;
      const connection = await createPeerConnection(callId);
      await connection.setRemoteDescription(offer);
      await applyPendingCandidates();
      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);

      await sendSignal('voice-answer', {
        from: selfId,
        callId,
        answer,
      });

      pendingOfferRef.current = null;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to answer the voice call.');
      resetCall();
    }
  }, [applyPendingCandidates, createPeerConnection, resetCall, selfId, sendSignal]);

  const declineCall = useCallback(async () => {
    if (!selfId || !pendingOfferRef.current) {
      resetCall();
      return;
    }

    await sendSignal('voice-end', {
      from: selfId,
      callId: pendingOfferRef.current.callId,
      reason: 'declined',
    });

    resetCall();
  }, [resetCall, selfId, sendSignal]);

  const endCall = useCallback(async () => {
    if (!selfId || !activeCallIdRef.current) {
      resetCall();
      return;
    }

    await sendSignal('voice-end', {
      from: selfId,
      callId: activeCallIdRef.current,
      reason: 'ended',
    });

    resetCall();
  }, [resetCall, selfId, sendSignal]);

  const toggleMute = useCallback(() => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
  }, [isMuted]);

  useEffect(() => {
    if (!selfId || !otherId || selfId === otherId) {
      return;
    }

    const channel = supabase.channel(`dm-voice:${getPairKey(selfId, otherId)}`, {
      config: {
        broadcast: { self: false },
      },
    });
    channelRef.current = channel;

    channel
      .on('broadcast', { event: 'voice-offer' }, ({ payload }) => {
        const signal = payload as CallSignalPayload;
        if (!signal || signal.from !== otherId || !signal.offer) {
          return;
        }

        pendingOfferRef.current = {
          callId: signal.callId,
          offer: signal.offer,
        };
        activeCallIdRef.current = signal.callId;
        setError(null);
        setPhase('incoming');
      })
      .on('broadcast', { event: 'voice-answer' }, async ({ payload }) => {
        const signal = payload as CallSignalPayload;
        if (!signal || signal.from !== otherId || !signal.answer || signal.callId !== activeCallIdRef.current || !peerConnectionRef.current) {
          return;
        }

        await peerConnectionRef.current.setRemoteDescription(signal.answer);
        await applyPendingCandidates();
      })
      .on('broadcast', { event: 'voice-ice' }, async ({ payload }) => {
        const signal = payload as CallSignalPayload;
        if (!signal || signal.from !== otherId || !signal.candidate || signal.callId !== activeCallIdRef.current) {
          return;
        }

        if (!peerConnectionRef.current || !peerConnectionRef.current.remoteDescription) {
          pendingCandidatesRef.current.push(signal.candidate);
          return;
        }

        await peerConnectionRef.current.addIceCandidate(signal.candidate);
      })
      .on('broadcast', { event: 'voice-end' }, ({ payload }) => {
        const signal = payload as CallSignalPayload;
        if (!signal || signal.from !== otherId || signal.callId !== activeCallIdRef.current) {
          return;
        }

        if (signal.reason === 'declined') {
          setError('Call declined.');
        }

        resetCall();
      })
      .subscribe((status) => {
        subscribedRef.current = status === 'SUBSCRIBED';
      });

    return () => {
      subscribedRef.current = false;
      channelRef.current = null;
      void channel.send({
        type: 'broadcast',
        event: 'voice-end',
        payload: {
          from: selfId,
          callId: activeCallIdRef.current,
          reason: 'ended',
        },
      });
      resetCall();
      supabase.removeChannel(channel);
    };
  }, [applyPendingCandidates, otherId, resetCall, selfId]);

  return {
    phase,
    isMuted,
    error,
    remoteAudioRef,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    clearError: () => setError(null),
  };
}
